import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowLeft, LogOut, CalendarDays, Settings2, Wand2 } from "lucide-react";
import { API } from "@/lib/data";
import { AdminBookingsTab } from "@/components/admin/AdminBookingsTab";
import { AdminSettingsTab } from "@/components/admin/AdminSettingsTab";
import { AdminVisualizerTab } from "@/components/admin/AdminVisualizerTab";

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "settings", label: "Site Settings", icon: Settings2 },
  { id: "visualizer", label: "Mockup Studio", icon: Wand2 },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("scc_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bookings");

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("scc_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back!");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("scc_admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#06130D] px-5" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-amber-500/25 bg-[#0B1D14]/90 p-8">
          <Lock className="mx-auto text-amber-400" size={32} />
          <h1 className="font-display mt-5 text-center text-2xl font-bold text-amber-50">Owner Login</h1>
          <p className="mt-2 text-center text-xs text-emerald-100/50">Southern Christmas Co. control room.</p>
          <input
            type="text"
            required
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Username"
            data-testid="admin-email-input"
            className="mt-6 w-full rounded-xl border border-emerald-500/25 bg-[#06130D]/70 px-4 py-3 text-sm text-amber-50 outline-none focus:border-amber-400/70"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
            className="mt-3 w-full rounded-xl border border-emerald-500/25 bg-[#06130D]/70 px-4 py-3 text-sm text-amber-50 outline-none focus:border-amber-400/70"
          />
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="mt-4 w-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 gold-glow disabled:opacity-60">
            {loading ? "Checking…" : "Log In"}
          </button>
          <Link to="/" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-emerald-100/50 hover:text-amber-300">
            <ArrowLeft size={13} /> Back to site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#06130D] px-5 py-10 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">Southern Christmas Co.</p>
            <h1 className="font-display mt-1 text-3xl font-black text-amber-50">Owner Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="flex items-center gap-2 rounded-full border border-amber-500/30 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-amber-300 transition-all hover:bg-amber-500/10">
              <ArrowLeft size={13} /> Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-rose-500/30 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-rose-300 transition-all hover:bg-rose-500/10">
              <LogOut size={13} /> Log Out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all ${
                tab === t.id
                  ? "border-amber-400 bg-amber-500/15 text-amber-300"
                  : "border-emerald-500/25 text-emerald-100/60 hover:border-amber-500/40 hover:text-amber-200"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "bookings" && <AdminBookingsTab token={token} />}
          {tab === "settings" && <AdminSettingsTab token={token} />}
          {tab === "visualizer" && <AdminVisualizerTab token={token} />}
        </div>
      </div>
    </main>
  );
}
